import { CarOutlined, CheckOutlined, CrownOutlined } from '@ant-design/icons'
import { useState } from 'react'
import MixpanelTracker from '../../../services/track/mixpanel'
import { PageLang } from '../pageLang'

type PlanTab = 'pro' | 'starter'

const CONTENT: Record<
  PageLang,
  {
    eyebrow: string
    headline: string
    subtext: string
    proTab: string
    starterTab: string
    proTitle: string
    proSubtitle: string
    proBadge: string
    proBullets: string[]
    starterTitle: string
    starterSubtitle: string
    starterBadge: string
    starterBullets: string[]
    seeEarningsBtn: string
    comparePlansBtn: string
  }
> = {
  EN: {
    eyebrow: 'Available plans',
    headline: 'Choose plan to earn with HAUP',
    subtext: 'Before you list your car, discover which plan is suitable for you.',
    proTab: 'Verified Pro',
    starterTab: 'Starter',
    proTitle: 'Verified Pro',
    proSubtitle: 'Automate more, earn more',
    proBadge: 'Managed by HAUP',
    proBullets: [
      'Listed on HAUP, Trip.com, Klook and other platforms',
      'Open for service for a minimum of 30 mins',
      '24-hour vehicle and rental car care',
      'Choose between two revenue sharing plans : 55% Owner 45% HAUP, 50% Owner 50% HAUP',
      'Need to park the car at HAUP stations',
      'No need to meet renters',
      'Equipped with an HAUP IoT system for advanced features',
    ],
    starterTitle: 'Starter',
    starterSubtitle: 'Manage manually, Stay flexibly',
    starterBadge: 'Managed by owner',
    starterBullets: [
      'Listed only on HAUP',
      'Renters can book 24 hours in advance',
      'You review and accept each booking',
      'No need to park the car at HAUP stations',
      'Set your own desired daily rental price',
    ],
    seeEarningsBtn: 'See how much you can earn',
    comparePlansBtn: 'Compare HAUP plans ›',
  },
  TH: {
    eyebrow: 'แผนที่เปิดให้บริการ',
    headline: 'เลือกแผนสร้างรายได้กับ HAUP',
    subtext: 'ก่อนลงทะเบียนรถของคุณ มาดูกันว่าแผนไหนเหมาะกับคุณที่สุด',
    proTab: 'Verified Pro',
    starterTab: 'Starter',
    proTitle: 'Verified Pro',
    proSubtitle: 'ให้ระบบช่วยจัดการ สร้างรายได้มากขึ้น',
    proBadge: 'ดูแลโดย HAUP',
    proBullets: [
      'ลงประกาศบน HAUP, Trip.com, Klook และแพลตฟอร์มอื่นๆ',
      'เปิดให้บริการขั้นต่ำ 30 นาที',
      'ดูแลรถและการเช่าตลอด 24 ชั่วโมง',
      'เลือกได้ระหว่างแผนแบ่งรายได้ 2 รูปแบบ : เจ้าของ 55% HAUP 45%, เจ้าของ 50% HAUP 50%',
      'ต้องจอดรถที่สถานี HAUP',
      'ไม่ต้องพบผู้เช่าเอง',
      'ติดตั้งระบบ HAUP IoT เพื่อฟีเจอร์ขั้นสูง',
    ],
    starterTitle: 'Starter',
    starterSubtitle: 'จัดการเอง ยืดหยุ่นได้ตามต้องการ',
    starterBadge: 'ดูแลโดยเจ้าของรถ',
    starterBullets: [
      'ลงประกาศเฉพาะบน HAUP',
      'ผู้เช่าสามารถจองล่วงหน้าได้ 24 ชั่วโมง',
      'คุณตรวจสอบและยืนยันการจองด้วยตัวเอง',
      'ไม่ต้องจอดรถที่สถานี HAUP',
      'กำหนดราคาเช่ารายวันได้เอง',
    ],
    seeEarningsBtn: 'ดูรายได้ที่คุณจะได้รับ',
    comparePlansBtn: 'เปรียบเทียบแผนของ HAUP ›',
  },
}

const Bullet = ({ text, dotColor }: { text: string; dotColor: string }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' }}>
    <div
      style={{
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        backgroundColor: dotColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginTop: '2px',
      }}
    >
      <CheckOutlined style={{ color: '#fff', fontSize: '9px' }} />
    </div>
    <p style={{ margin: 0, fontSize: '13.5px', color: '#333', lineHeight: '1.5' }}>{text}</p>
  </div>
)

const IconBox = ({ icon, backgroundColor }: { icon: React.ReactNode; backgroundColor: string }) => (
  <div
    style={{
      width: '32px',
      height: '32px',
      borderRadius: '9px',
      backgroundColor,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    {icon}
  </div>
)

export interface ChoosePlanSectionProps {
  lang: PageLang
  onSeeEarnings: () => void
  onComparePlans: () => void
}

export const ChoosePlanSection = ({
  lang,
  onSeeEarnings,
  onComparePlans,
}: ChoosePlanSectionProps) => {
  const t = CONTENT[lang]
  const [activeTab, setActiveTab] = useState<PlanTab>('pro')

  const tabStyle = (tab: PlanTab): React.CSSProperties => ({
    flex: 1,
    textAlign: 'center',
    padding: '10px 0',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    backgroundColor: activeTab === tab ? '#F4F5F9' : 'transparent',
    color: activeTab === tab ? '#0048B3' : '#8C97A7',
  })

  return (
    <div style={{ padding: '32px 16px 8px' }}>
      <div
        style={{
          display: 'inline-block',
          backgroundColor: '#EAF1FF',
          color: '#0048B3',
          fontSize: '12px',
          fontWeight: 600,
          padding: '4px 12px',
          borderRadius: '12px',
          marginBottom: '12px',
        }}
      >
        {t.eyebrow}
      </div>
      <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#212427', margin: '0 0 6px' }}>
        {t.headline}
      </h2>
      <p style={{ fontSize: '13px', color: '#667085', lineHeight: '1.5', marginBottom: '18px' }}>
        {t.subtext}
      </p>

      <div
        style={{
          display: 'flex',
          backgroundColor: '#fff',
          border: '1px solid #E4E7EC',
          borderRadius: '14px',
          padding: '4px',
          marginBottom: '16px',
        }}
      >
        <div onClick={() => setActiveTab('pro')} style={tabStyle('pro')}>
          {t.proTab}
        </div>
        <div onClick={() => setActiveTab('starter')} style={tabStyle('starter')}>
          {t.starterTab}
        </div>
      </div>

      {activeTab === 'pro' ? (
        <div
          style={{
            border: '1px solid #E4E7EC',
            borderRadius: '14px',
            padding: '18px',
            marginBottom: '16px',
            backgroundColor: '#fff',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              marginBottom: '14px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <IconBox
                backgroundColor="#E6F3FF"
                icon={<CrownOutlined style={{ color: '#0048B3', fontSize: '15px' }} />}
              />
              <div>
                <div style={{ fontSize: '16px', fontWeight: 700, color: '#212427' }}>
                  {t.proTitle}
                </div>
                <div style={{ fontSize: '12px', color: '#8C97A7', marginTop: '1px' }}>
                  {t.proSubtitle}
                </div>
              </div>
            </div>
            <span
              style={{
                backgroundColor: '#E7F7EC',
                color: '#1A8F4C',
                fontSize: '11px',
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: '12px',
                whiteSpace: 'nowrap',
              }}
            >
              {t.proBadge}
            </span>
          </div>
          {t.proBullets.map((text) => (
            <Bullet key={text} text={text} dotColor="#0048B3" />
          ))}
        </div>
      ) : (
        <div
          style={{
            border: '1px solid #E4E7EC',
            borderRadius: '14px',
            padding: '18px',
            marginBottom: '16px',
            backgroundColor: '#fff',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              marginBottom: '14px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <IconBox
                backgroundColor="#F4F5F9"
                icon={<CarOutlined style={{ color: '#212427', fontSize: '15px' }} />}
              />
              <div>
                <div style={{ fontSize: '16px', fontWeight: 700, color: '#212427' }}>
                  {t.starterTitle}
                </div>
                <div style={{ fontSize: '12px', color: '#8C97A7', marginTop: '1px' }}>
                  {t.starterSubtitle}
                </div>
              </div>
            </div>
            <span
              style={{
                backgroundColor: '#F4F5F9',
                color: '#667085',
                fontSize: '11px',
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: '12px',
                whiteSpace: 'nowrap',
              }}
            >
              {t.starterBadge}
            </span>
          </div>
          {t.starterBullets.map((text) => (
            <Bullet key={text} text={text} dotColor="#4B5563" />
          ))}
        </div>
      )}

      <button
        onClick={() => {
          MixpanelTracker.track('Login Page See Earnings Click', { plan: activeTab })
          onSeeEarnings()
        }}
        style={{
          width: '100%',
          height: '48px',
          backgroundColor: '#0048B3',
          border: 'none',
          borderRadius: '25px',
          color: '#fff',
          fontSize: '15px',
          fontWeight: 500,
          cursor: 'pointer',
          marginBottom: '12px',
        }}
      >
        {t.seeEarningsBtn}
      </button>
      <div
        onClick={() => {
          MixpanelTracker.track('Login Page Compare Plans Click')
          onComparePlans()
        }}
        style={{
          textAlign: 'center',
          color: '#0048B3',
          fontSize: '14px',
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        {t.comparePlansBtn}
      </div>
    </div>
  )
}

export default ChoosePlanSection
